import React, { useMemo } from 'react';

import { Flex, Label, Select } from '@lucidhq/lucidium';
import { useField, Field } from '@lucidhq/lucidium/components/FormV2';
import { useFeatureFlag } from '@lucidhq/ui-feature-flags';

import { useFetchSocialPlatforms } from '~/components/Shared/hooks/useFetchSocialPlatforms';
import { IM_FLAGS } from '~/impact-measurement/constants/featureFlags';
import { SocialPlatform } from '~/types';
import { t } from '~/utils/i18n';

import { EndDateField } from '../EndDateField';
import { StartDateField } from '../StartDateField';

type SocialPlatformFieldProps = {
  isSocialChecked: boolean;
  readOnly: boolean;
};

type SocialPlatformOption = SocialPlatform & {
  label: string;
  value: number;
};

const mapSocialPlatformOptions = (
  options: SocialPlatform[]
): SocialPlatformOption[] =>
  options.map(option => ({
    ...option,
    label: option.social_platform_name,
    value: option.id
  }));

export const SocialPlatformsFields = ({
  isSocialChecked,
  readOnly
}: SocialPlatformFieldProps) => {
  const [socialField, socialMeta, socialHelpers] = useField(
    'draft_settings.social_platforms'
  );
  const { data, isLoading, error } = useFetchSocialPlatforms({
    isEnabled: isSocialChecked
  });

  const mappedSocialPlatforms = useMemo(
    () => mapSocialPlatformOptions(data?.items ?? []),
    [data]
  );
  const { treatment } = useFeatureFlag(IM_FLAGS.FF_IM_EXTRA_SOCIAL_FIELDS);

  const socialErrors = useMemo(() => {
    if (error) return t('forms.placeholders.errorLoadingSocialPlatforms');

    return socialMeta.error;
  }, [error, socialMeta.error]);

  if (!isSocialChecked) return null;

  return (
    <>
      <Flex
        id="social-platform-field"
        lss={{
          flexDirection: 'column',
          mb: 'md'
        }}
      >
        <Label lss={{ my: 'sm' }} required>
          {t(`forms.labels.socialPlatforms`)}
        </Label>
        <Select
          isMulti
          id="social-platform"
          name="draft_settings.social_platforms"
          value={socialField?.value}
          error={socialErrors}
          disabled={isLoading || readOnly}
          placeholder={
            isLoading
              ? t('forms.placeholders.loadingSocialPlatforms')
              : t('forms.placeholders.socialPlatforms')
          }
          labelKey="label"
          valueKey="value"
          options={mappedSocialPlatforms}
          onChange={(option: number[] | null | undefined) => {
            const val = [...new Set(option)];
            socialHelpers.setValue(val);
          }}
          lss={{ width: '100%' }}
          styles={{
            container: (baseStyles: any) => ({
              ...baseStyles,
              display: 'flex',
              flexDirection: 'column'
            }),
            menu: (baseStyles: any) => ({
              ...baseStyles,
              overflow: 'auto',
              maxHeight: '160px',
              display: 'flex',
              width: 'fill-available'
            }),
            menuList: (baseStyles: any) => ({
              ...baseStyles,
              width: 'fill-available'
            }),
            option: (baseStyles: any) => ({
              ...baseStyles,
              display: 'flex',
              width: 'fit-content'
            })
          }}
        />
      </Flex>
      {treatment === 'on' && (
        <Flex lss={{ flexDirection: 'column', gap: 'md' }}>
          <Field
            label={t('forms.labels.socialBrandName')}
            name="social_configuration.brand_name"
            type="text"
            placeholder={t('forms.placeholders.brandName')}
            disabled={readOnly}
            required
          />

          {/* <Field
            label={t('forms.labels.socialHandles')}
            name="social_configuration.handles"
            type="text"
            placeholder={t('forms.placeholders.handles')}
            disabled={readOnly}
          /> */}
          <Flex
            lss={{ justifyContent: 'space-between', paddingBottom: '8.5rem' }}
          >
            <StartDateField
              name="social_configuration.ads_start_date"
              label="forms.labels.adsStartDate"
              title="campaignManager.campaignDetails.tooltips.adsStartDate"
              readOnly={readOnly}
            />
            <EndDateField
              name="social_configuration.ads_end_date"
              label="forms.labels.adsEndDate"
              title="campaignManager.campaignDetails.tooltips.adsEndDate"
              readOnly={readOnly}
            />
          </Flex>
        </Flex>
      )}
    </>
  );
};
